import React, { useState } from "react";
import SelectType from "./components/SelectType";
import UploadCSV from "./components/UploadCSV";
import MapFields from "./components/MapFields";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Stepper from "./components/Stepper";
import { QRCodeCanvas } from "qrcode.react";
import { generateQRContent } from "./utils/generateQRContent";
import { downloadQRCodesAsZip } from "./utils/downloadQRCodes";
import { downloadQRCodesAsExcel } from './utils/downloadExcel';
import { downloadQRCodesAsPrintable } from './utils/downloadPrintable';
import { colors, typography, spacing, components, shadows, radii } from './styles/shared';
import { ArrowRight } from 'lucide-react';
import PrintFormatDialog from "./components/PrintFormatDialog";
import { QRTypeFields } from './config/qrTypes';
import QRCodeCustomizer from "./components/QRCodeCustomizer";

// Common styles
const styles = {
  app: {
    minHeight: "100vh",
    background: `
      linear-gradient(135deg, ${colors.primary[50]} 0%, white 25%, white 75%, ${colors.primary[50]} 100%),
      radial-gradient(circle at 25% 100%, ${colors.primary[50]} 0%, transparent 25%),
      radial-gradient(circle at 75% 0%, ${colors.primary[50]} 0%, transparent 25%)
    `,
    position: 'relative',
  },
  mainContent: {
    position: 'relative',
    backgroundColor: colors.background.page,
    borderTop: `1px solid ${colors.gray[200]}`,
    boxShadow: `0 -1px 0 ${colors.gray[100]}, 0 4px 6px -1px rgba(0, 0, 0, 0.05)`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-50px',
      left: 0,
      right: 0,
      height: '50px',
      background: `linear-gradient(to bottom, transparent, ${colors.gray[50]})`,
      pointerEvents: 'none',
    },
  },
  container: {
    ...components.container,
    paddingTop: spacing[10],
    paddingBottom: spacing[10],
  },
  section: {
    ...components.card,
    marginBottom: spacing[8],
  },
  button: {
    ...components.button.base,
  },
  generateButton: {
    ...components.button.base,
    ...components.button.primary,
    marginTop: spacing[5],
    width: '100%',
    maxWidth: '400px',
    display: 'block',
    margin: `${spacing[5]} auto`,
  },
  disabledButton: {
    ...components.button.disabled,
  },
  downloadButton: {
    ...components.button.base,
    ...components.button.success,
  },
  qrCodesContainer: {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '24px',
  },
  qrCodesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)', // 4 columns by default
    gap: '24px',
    '@media (max-width: 1200px)': {
      gridTemplateColumns: 'repeat(3, 1fr)', // 3 columns on medium screens
    },
    '@media (max-width: 900px)': {
      gridTemplateColumns: 'repeat(2, 1fr)', // 2 columns on smaller screens
    },
    '@media (max-width: 600px)': {
      gridTemplateColumns: 'repeat(1, 1fr)', // 1 column on mobile
    },
  },
  qrCodeCard: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    aspectRatio: '1', // Make cards square
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
  },
  qrCodeWrapper: {
    width: '80%', // Relative to card size
    aspectRatio: '1',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '12px',
  },
  qrCodeLabel: {
    fontSize: '14px',
    color: '#6b7280',
    textAlign: 'center',
    padding: '4px 0',
  },
  sectionTitle: {
    ...typography.h3,
    color: colors.gray[900],
    margin: 0,
  },
  fieldChip: {
    padding: `${spacing[1]} ${spacing[3]}`,
    backgroundColor: colors.gray[100],
    borderRadius: radii.full,
    ...typography.body2,
    color: colors.gray[700],
  },
  statusBar: {
    display: 'flex',
    gap: spacing[6],
    padding: `${spacing[3]} ${spacing[5]}`,
    backgroundColor: colors.gray[50],
    borderRadius: radii.lg,
    marginBottom: spacing[6],
    border: `1px solid ${colors.gray[200]}`,
  },
  statusItem: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
    ...typography.body2,
  },
  statusLabel: {
    color: colors.gray[500],
    fontWeight: '500',
  },
  statusValue: {
    color: colors.gray[900],
    fontWeight: '600',
  },
  blogSection: {
    backgroundColor: colors.background.page,
    padding: `${spacing[16]} 0`,
    borderTop: `1px solid ${colors.gray[200]}`,
    position: 'relative',
  },
  blogContainer: {
    ...components.container,
    maxWidth: '1200px',
  },
  blogHeader: {
    marginBottom: spacing[8],
  },
  blogTitle: {
    ...typography.h2,
    color: colors.primary[900],
    marginBottom: spacing[4],
  },
  blogSubtitle: {
    ...typography.body1,
    color: colors.gray[600],
    marginBottom: spacing[8],
  },
  blogGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: spacing[8],
    marginBottom: spacing[8],
  },
  blogCard: {
    ...components.card,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    transition: 'box-shadow 0.2s ease-in-out',
    '&:hover': {
      boxShadow: shadows.lg,
    },
  },
  blogImage: {
    width: '100%',
    height: '200px',
    objectFit: 'cover',
    borderRadius: `${radii.lg} ${radii.lg} 0 0`,
  },
  imageCredit: {
    ...typography.caption,
    color: colors.gray[500],
    fontSize: '10px',
    textAlign: 'right',
    padding: `${spacing[1]} ${spacing[2]}`,
  },
  blogContent: {
    padding: spacing[6],
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  blogCardTitle: {
    ...typography.h4,
    color: colors.gray[900],
    marginBottom: spacing[3],
  },
  blogCardText: {
    ...typography.body2,
    color: colors.gray[600],
    marginBottom: spacing[4],
  },
  blogCardMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
    marginTop: 'auto',
  },
  blogCardAuthor: {
    ...typography.body2,
    color: colors.gray[600],
  },
  blogCardLink: {
    ...components.button.base,
    ...components.button.secondary,
    textDecoration: 'none',
    display: 'inline-block',
    marginTop: spacing[4],
  },
  viewAllLink: {
    ...typography.body1,
    color: colors.primary[700],
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
    '&:hover': {
      color: colors.primary[800],
    },
  },
  footer: {
    backgroundColor: colors.gray[900],
    color: colors.gray[100],
    padding: `${spacing[12]} 0`,
    position: 'relative',
  },
  footerContainer: {
    ...components.container,
    maxWidth: '1200px',
  },
  privacyPolicy: {
    backgroundColor: colors.gray[800],
    borderRadius: radii.xl,
    padding: spacing[8],
    marginBottom: spacing[8],
  },
  privacyTitle: {
    ...typography.h3,
    color: colors.gray[100],
    marginBottom: spacing[4],
  },
  privacyText: {
    ...typography.body1,
    color: colors.gray[300],
    marginBottom: spacing[6],
  },
  privacyList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[4],
  },
  privacyItem: {
    display: 'flex',
    gap: spacing[3],
    alignItems: 'flex-start',
    color: colors.gray[300],
    ...typography.body2,
  },
  privacyItemTitle: {
    color: colors.gray[100],
    fontWeight: '600',
  },
  copyright: {
    ...typography.body2,
    color: colors.gray[400],
    textAlign: 'center',
    borderTop: `1px solid ${colors.gray[700]}`,
    paddingTop: spacing[6],
    marginTop: spacing[6],
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing[2],
    marginTop: spacing[6],
  },
  pageButton: {
    ...components.button.base,
    padding: `${spacing[2]} ${spacing[3]}`,
    minWidth: '40px',
    height: '40px',
    borderRadius: '8px',
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pageButtonActive: {
    backgroundColor: colors.primary[50],
    color: colors.primary[600],
    borderColor: colors.primary[200],
  },
};

const STEPS = [
  "Select Type",
  "Upload CSV",
  "Map Fields",
  "Generate QR Codes"
];

function App() {
  const [currentStep, setCurrentStep] = useState(0);
  const [qrType, setQrType] = useState("");
  const [csvHeaders, setCSVHeaders] = useState([]);
  const [csvData, setCSVData] = useState([]);
  const [mappedFields, setMappedFields] = useState({});
  const [qrCodes, setQrCodes] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [fileName, setFileName] = useState("");
  const [isExcelDownloading, setIsExcelDownloading] = useState(false);
  const [isPrintableDownloading, setIsPrintableDownloading] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12; // Show 12 QR codes per page
  const [qrCodeFgColor, setQrCodeFgColor] = useState("#000000");
  const [qrCodeBgColor, setQrCodeBgColor] = useState("#FFFFFF");
  const [qrLogo, setQrLogo] = useState(null);
  const [qrLogoSize, setQrLogoSize] = useState(30);

  const handleCSVUpload = (headers, data, file) => {
    if (!headers || !Array.isArray(headers)) {
      console.error("Invalid headers:", headers);
      return;
    }
    console.log("Headers received:", headers);
    setCSVHeaders(headers);
    setCSVData(data);
    setFileName(file.name);
    setMappedFields({});
    setCompletedSteps(prev => {
      const newSet = new Set(prev);
      newSet.add(1); // Mark Upload CSV as completed
      return newSet;
    });
    setCurrentStep(2); // Move to Map Fields step
  };

  const handleGenerateQRCodes = () => {
    const codes = csvData.map((row, index) => ({
      id: index,
      content: generateQRContent(qrType, row, mappedFields),
    }));
    setQrCodes(codes);
    setCompletedSteps(prev => {
      const newSet = new Set(prev);
      newSet.add(2); // Mark Map Fields as completed
      return newSet;
    });
    setCurrentStep(3); // Move to QR Codes step
  };

  const handleTypeSelect = (type) => {
    setQrType(type);
    setCompletedSteps(prev => {
      const newSet = new Set(prev);
      newSet.add(0); // Mark Select Type as completed
      return newSet;
    });
    setCurrentStep(1); // Move to Upload CSV step
  };

  const handleDownloadQRCodes = async () => {
    setIsDownloading(true);
    try {
      await downloadQRCodesAsZip(qrCodes, qrCodeFgColor, qrCodeBgColor, qrLogo, qrLogoSize);
    } catch (error) {
      console.error('Error downloading QR codes:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadExcel = async () => {
    setIsExcelDownloading(true);
    try {
      await downloadQRCodesAsExcel(qrCodes, `qr-codes-${fileName || 'export'}.xlsx`, qrCodeFgColor, qrCodeBgColor, qrLogo, qrLogoSize);
    } catch (error) {
      console.error('Error downloading Excel file:', error);
    } finally {
      setIsExcelDownloading(false);
    }
  };

  const handleDownloadPrintable = async (format) => {
    setIsPrintableDownloading(true);
    try {
      await downloadQRCodesAsPrintable(qrCodes, `qr-codes-${fileName || 'export'}-printable.pdf`, format, qrCodeFgColor, qrCodeBgColor, qrLogo, qrLogoSize);
    } catch (error) {
      console.error('Error downloading printable format:', error);
    } finally {
      setIsPrintableDownloading(false);
    }
  };

  const isGenerateButtonEnabled = () => {
    if (qrType === 'plain_text') {
      // For plain text, enable if at least one column is selected
      return mappedFields.columns && mappedFields.columns.length > 0;
    }

    // Get required fields for the current QR type
    const requiredFields = QRTypeFields[qrType] || [];
    
    // Check if all required fields are mapped and have values
    return requiredFields.every(field => 
      mappedFields[field] && mappedFields[field].trim() !== ''
    );
  };

  const setCurrentStepWithReset = (step) => {
    setCurrentStep(step);
    // Clear completion status for steps after the selected step
    setCompletedSteps(prev => {
      const newSet = new Set([...prev].filter(s => s < step));
      return newSet;
    });
    
    // Reset state based on step
    if (step < 2) {
      setMappedFields({});
    }
    if (step < 3) {
      setQrCodes([]);
    }
  };

  const getTypeDisplayName = (type) => {
    if (!type) return '';
    return type.split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const renderStatusBar = () => {
    if (currentStep === 0) return null;

    return (
      <div style={styles.statusBar}>
        {qrType && (
          <div style={styles.statusItem}>
            <span style={styles.statusLabel}>Type:</span>
            <span style={styles.statusValue}>{getTypeDisplayName(qrType)}</span>
          </div>
        )}
        {fileName && (
          <div style={styles.statusItem}>
            <span style={styles.statusLabel}>File:</span>
            <span style={styles.statusValue}>{fileName}</span>
          </div>
        )}
      </div>
    );
  };

  const renderQRCodesWithPagination = () => {
    const totalPages = Math.ceil(qrCodes.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentQRCodes = qrCodes.slice(startIndex, endIndex);

    return (
      <div style={styles.qrCodesContainer}>
        <QRCodeCustomizer
          fgColor={qrCodeFgColor}
          bgColor={qrCodeBgColor}
          onFgColorChange={setQrCodeFgColor}
          onBgColorChange={setQrCodeBgColor}
          onLogoChange={setQrLogo}
          onLogoSizeChange={setQrLogoSize}
        />
        <div style={styles.qrCodesGrid}>
          {currentQRCodes.map((qr, index) => (
            <div 
              key={qr.id} 
              style={styles.qrCodeCard}
              id={`qr-code-${qr.id}`}
            >
              <div style={styles.qrCodeWrapper}>
                <QRCodeCanvas
                  value={qr.content}
                  size={180}
                  level="M"
                  includeMargin={true}
                  fgColor={qrCodeFgColor}
                  bgColor={qrCodeBgColor}
                  imageSettings={qrLogo ? {
                    src: qrLogo,
                    height: Math.floor(180 * (qrLogoSize / 100)),
                    width: Math.floor(180 * (qrLogoSize / 100)),
                    excavate: true,
                  } : undefined}
                />
              </div>
              <p style={styles.qrCodeLabel}>QR Code {startIndex + index + 1}</p>
            </div>
          ))}
        </div>
        {totalPages > 1 && (
          <div style={styles.pagination}>
            <button
              style={styles.pageButton}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              ←
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                style={{
                  ...styles.pageButton,
                  ...(page === currentPage && styles.pageButtonActive),
                }}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              style={styles.pageButton}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              →
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div style={styles.section} id="generate-section">
            <SelectType 
              selectedType={qrType}
              onSelect={handleTypeSelect}
            />
          </div>
        );
      case 1:
        return (
          <div style={styles.section}>
            <UploadCSV 
              qrType={qrType}
              onUpload={handleCSVUpload}
            />
          </div>
        );
      case 2:
        return (
          <>
            {csvHeaders.length > 0 && qrType && (
              <div style={styles.section}>
                <div style={{ marginBottom: spacing[5] }}>
                  <h3 style={styles.sectionTitle}>Available Fields</h3>
                  <div style={{ 
                    display: "flex", 
                    flexWrap: "wrap", 
                    gap: spacing[2],
                    marginTop: spacing[3],
                  }}>
                    {csvHeaders.map(header => (
                      <span key={header} style={styles.fieldChip}>
                        {header}
                      </span>
                    ))}
                  </div>
                </div>
                <MapFields
                  qrType={qrType}
                  csvHeaders={csvHeaders}
                  csvData={csvData}
                  mappedFields={mappedFields}
                  setMappedFields={setMappedFields}
                />
                <button
                  style={{
                    ...styles.button,
                    ...styles.generateButton,
                    ...(!isGenerateButtonEnabled() && styles.disabledButton)
                  }}
                  onClick={handleGenerateQRCodes}
                  disabled={!isGenerateButtonEnabled()}
                >
                  {isGenerateButtonEnabled() ? "Generate QR Codes" : "Map Required Fields First"}
                </button>
              </div>
            )}
          </>
        );
      case 3:
        return (
          <div style={styles.section}>
            <div style={styles.statusBar}>
              <div style={styles.statusItem}>
                <span style={styles.statusLabel}>QR Codes:</span>
                <span style={styles.statusValue}>{qrCodes.length}</span>
              </div>
              <div style={styles.statusItem}>
                <span style={styles.statusLabel}>Source:</span>
                <span style={styles.statusValue}>{fileName}</span>
              </div>
            </div>
            <div style={{
              display: 'flex',
              gap: spacing[4],
              marginBottom: spacing[6],
            }}>
              <button
                style={{
                  ...styles.downloadButton,
                  ...(isDownloading && styles.disabledButton),
                }}
                onClick={handleDownloadQRCodes}
                disabled={isDownloading}
              >
                {isDownloading ? 'Preparing ZIP...' : 'Download as ZIP'}
              </button>
              <button
                style={{
                  ...styles.downloadButton,
                  ...(isExcelDownloading && styles.disabledButton),
                }}
                onClick={handleDownloadExcel}
                disabled={isExcelDownloading}
              >
                {isExcelDownloading ? 'Preparing Excel...' : 'Download as Excel'}
              </button>
              <button
                style={{
                  ...styles.downloadButton,
                  ...(isPrintableDownloading && styles.disabledButton),
                }}
                onClick={() => setShowPrintDialog(true)}
                disabled={isPrintableDownloading}
              >
                {isPrintableDownloading ? 'Preparing PDF...' : 'Download Printable Format'}
              </button>
            </div>
            {renderQRCodesWithPagination()}
            {showPrintDialog && (
              <PrintFormatDialog
                onClose={() => setShowPrintDialog(false)}
                onDownload={handleDownloadPrintable}
              />
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div style={styles.app}>
      <Header />
      <Hero />
      <main id="generate-section" style={styles.mainContent}>
        <div style={styles.container}>
          <Stepper
            steps={STEPS}
            currentStep={currentStep}
            setCurrentStep={setCurrentStepWithReset}
            isStepComplete={(step) => completedSteps.has(step)}
          />
          {renderStatusBar()}
          {renderCurrentStep()}
        </div>
      </main>
      
      {/* Blog Section */}
      <section style={styles.blogSection}>
        <div style={styles.blogContainer}>
          <div style={styles.blogHeader}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing[6] }}>
              <div>
                <h2 style={styles.blogTitle}>Latest from Our Blog</h2>
                <p style={styles.blogSubtitle}>Expert insights on document automation and digital transformation</p>
              </div>
              <a 
                href="https://www.build-automate-ai-repeat.com/blogs"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  ...styles.blogCardLink,
                  marginTop: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing[2],
                }}
              >
                View all posts
                <ArrowRight size={18} />
              </a>
            </div>
          </div>
          <div style={styles.blogGrid}>
            <article style={styles.blogCard}>
              <div style={{ position: 'relative' }}>
                <img 
                  src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40"
                  alt="Approval Automation - Business workflow"
                  style={styles.blogImage}
                />
                <div style={styles.imageCredit}>
                  Photo by <a href="https://unsplash.com/@sctgrhm" target="_blank" rel="noopener noreferrer">Scott Graham</a> on <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer">Unsplash</a>
                </div>
              </div>
              <div style={styles.blogContent}>
                <h3 style={styles.blogCardTitle}>Automate Approvals Using Power Automate</h3>
                <p style={styles.blogCardText}>Step-by-step guide to implementing automated approval workflows in your organization.</p>
                <div style={styles.blogCardMeta}>
                  <span style={styles.blogCardAuthor}>Abhishek Gawde</span>
                </div>
                <a 
                  href="https://www.build-automate-ai-repeat.com/blogs/automate-approvals-using-power-automate"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.blogCardLink}
                >
                  Read more
                </a>
              </div>
            </article>

            <article style={styles.blogCard}>
              <div style={{ position: 'relative' }}>
                <img 
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71"
                  alt="Asset Management - Modern IT infrastructure"
                  style={styles.blogImage}
                />
                <div style={styles.imageCredit}>
                  Photo by <a href="https://unsplash.com/@tvick" target="_blank" rel="noopener noreferrer">Taylor Vick</a> on <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer">Unsplash</a>
                </div>
              </div>
              <div style={styles.blogContent}>
                <h3 style={styles.blogCardTitle}>Asset Management App - User Interface</h3>
                <p style={styles.blogCardText}>Learn how to build an intuitive and efficient user interface for asset management applications.</p>
                <div style={styles.blogCardMeta}>
                  <span style={styles.blogCardAuthor}>Abhishek Gawde</span>
                </div>
                <a 
                  href="https://www.build-automate-ai-repeat.com/blogs/asset-management-app-user-interface"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.blogCardLink}
                >
                  Read more
                </a>
              </div>
            </article>

            <article style={styles.blogCard}>
              <div style={{ position: 'relative' }}>
                <img 
                  src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85"
                  alt="Invoice Automation - Digital Business"
                  style={styles.blogImage}
                />
                <div style={styles.imageCredit}>
                  Photo by <a href="https://unsplash.com/@homajob" target="_blank" rel="noopener noreferrer">Scott Graham</a> on <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer">Unsplash</a>
                </div>
              </div>
              <div style={styles.blogContent}>
                <h3 style={styles.blogCardTitle}>How to Automate Invoice Generation with Power Automate</h3>
                <p style={styles.blogCardText}>Discover how to automate invoice generation and streamline your billing process.</p>
                <div style={styles.blogCardMeta}>
                  <span style={styles.blogCardAuthor}>Abhishek Gawde</span>
                </div>
                <a 
                  href="https://www.build-automate-ai-repeat.com/blogs/how-to-automate-invoice-generation-with-power-automate"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.blogCardLink}
                >
                  Read more
                </a>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContainer}>
          <div style={styles.privacyPolicy}>
            <h2 style={styles.privacyTitle}>Privacy Policy</h2>
            <p style={styles.privacyText}>
              At Bulk QR Generator, we prioritize your privacy and data security. Here's how we ensure your peace of mind:
            </p>
            <ul style={styles.privacyList}>
              <li style={styles.privacyItem}>
                <span style={styles.privacyItemTitle}>Complete Local Processing:</span>
                {" "}All file uploads, data processing, and document generation are performed directly in your browser. Your files never leave your device.
              </li>
              <li style={styles.privacyItem}>
                <span style={styles.privacyItemTitle}>No Data Stored:</span>
                {" "}We don't store your files or data on any servers. You stay in full control of your information at all times.
              </li>
              <li style={styles.privacyItem}>
                <span style={styles.privacyItemTitle}>Secure Dependencies:</span>
                {" "}We use industry-standard libraries, regularly updated to address vulnerabilities and maintain robust security.
              </li>
              <li style={styles.privacyItem}>
                <span style={styles.privacyItemTitle}>HTTPS Encryption:</span>
                {" "}The application is served over HTTPS, ensuring a secure connection while you use our tool.
              </li>
            </ul>
            <p style={{...styles.privacyText, marginTop: spacing[6], marginBottom: 0}}>
              We believe that data privacy isn't just a feature—it's your right. With us, you can work confidently, knowing your information stays private and secure.
            </p>
          </div>
          <div style={styles.copyright}>
            © 2025 Bulk QR Generator. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
